import { useMutation } from '@apollo/client';
import React, { useCallback, useState } from 'react';
import Modal, { ModalBody, ModalFooter } from '../../components/Modal';
import { Flex } from 'rebass';
import { Checkbox, Input, Label, Textarea } from '@rebass/forms';
import Button from '../../components/Button/Button';
import PropTypes from 'prop-types';
import useNotify from '../../hooks/useNotify';
import useSessionInfo from '../../hooks/useSessionInfo';
import { format } from 'date-fns';
import FormColumn from '../../components/Form/FormColumn';
import FormRow from '../../components/Form/FormRow';
import Form from '../../components/Form/Form';
import { ProfileType } from '../../components/Profile/types';
import DataTable from '../../components/Table/DataTable';
import { Column } from '../../hooks/useTablePages';
import { booleanToLabel } from '@ecosconnect/core/src/helpers/string';
import { gql } from '@/modes/company/gql';

type EmailBackflowTestResultsModalProps = {
  onClose: (actionTaken?: boolean) => void;
  assignmentIds: string[] | readonly string[];
};

const EMAIL_TEST_REPORTS = gql(`
  mutation EmailBackflowTestReports(
    $assignmentIds: [ID!]!
    $subject: String!
    $message: String!
    $sendToCustomers: Boolean
    $sendToWaterSuppliers: Boolean
    $cc: String
    $bccMe: Boolean = false
    $setToSubmitted: Boolean = false
  ) {
    emailBackflowTestReports(
      assignmentIds: $assignmentIds
      subject: $subject
      message: $message
      sendToCustomers: $sendToCustomers
      sendToWaterSuppliers: $sendToWaterSuppliers
      cc: $cc
      bccMe: $bccMe
      setToSubmitted: $setToSubmitted
    ) {
      emailResults {
        jobIds
        assignmentIds
        recipientProfile {
          id
          name
          emails {
            value
          }
        }
        sent
        error
      }
      statusChanges {
        assignmentId
        success
        error
      }
    }
  }
`);

type EmailTestReportInput = {
  assignmentIds: readonly string[] | string[];
  subject?: string;
  message?: string;
  sendToCustomers: boolean;
  sendToWaterSuppliers: boolean;
  cc?: string;
  bccMe: boolean;
  setToSubmitted: boolean;
};

type EmailTestReportResult = {
  jobIds: number[];
  assignmentIds: number[];
  recipientProfile: {
    id: number;
    type: ProfileType;
    name: string;
    firstName?: string;
    lastName?: string;
    emails: { value: string }[];
  };
  sent: boolean;
  error?: string;
};

type StatusChangeResult = {
  assignmentId: number;
  success: boolean;
  error?: string;
};

type MutationResult = {
  emailResults: EmailTestReportResult[];
  statusChanges: StatusChangeResult[];
};

export const emailResultColumns: Column[] = [
  {
    header: 'To',
    accessor: 'recipientProfile.name',
    width: 160,
  },
  {
    header: 'Email',
    id: 'email',
    accessor: (row: EmailTestReportResult) => {
      return row.recipientProfile.emails[0]?.value ?? '';
    },
    width: 200,
  },
  {
    header: 'Reports',
    accessor: 'jobIds.length',
    width: 75,
  },
  {
    header: 'Success',
    id: 'sent',
    accessor: (row: EmailTestReportResult) => {
      return booleanToLabel(row.sent);
    },
    width: 75,
  },
  {
    header: 'Error',
    accessor: 'error',
    width: 246,
  },
];

const EmailBackflowTestReportsResult: React.FC<{ result: MutationResult }> = ({
  result,
}) => {
  return (
    <Flex maxHeight="calc(100vh - 210px)">
      <DataTable columns={emailResultColumns} items={result.emailResults} />
    </Flex>
  );
};

const EmailBackflowTestReportsModal: React.FC<
  EmailBackflowTestResultsModalProps
> = ({ onClose, assignmentIds }) => {
  const onCancel = useCallback(() => onClose(), []);
  const onActionComplete = useCallback(() => onClose(true), []);
  const { showError } = useNotify();
  const sessionInfo = useSessionInfo();
  const plural = assignmentIds.length > 1 ? 's' : '';

  const [emailBackflowTestReports, { data, loading, error }] = useMutation<
    {
      emailBackflowTestReports: MutationResult;
    },
    EmailTestReportInput
  >(EMAIL_TEST_REPORTS, {
    onError: useCallback((error) => {
      showError(`Error sending test report${plural}`, error);
    }, []),
  });

  const [emailTestReportInput, setEmailTestReportInput] =
    useState<EmailTestReportInput>({
      assignmentIds,
      subject: `Backflow Test Report${plural} from ${
        sessionInfo.me?.name
      } - ${format(Date.now(), 'M/dd/yyyy')}`,
      message: `Please view the attached report${plural}`,
      sendToCustomers: true,
      sendToWaterSuppliers: false,
      cc: undefined,
      bccMe: false,
      setToSubmitted: false,
    });

  const onInputChange = useCallback((event: any) => {
    if (event.target.type === 'checkbox') {
      setEmailTestReportInput((prevState) => {
        return {
          ...prevState,
          [event.target.name]: event.target.checked,
        };
      });
    } else {
      setEmailTestReportInput((prevState) => {
        return {
          ...prevState,
          [event.target.name]: event.target.value,
        };
      });
    }
  }, []);

  if (!loading && data?.emailBackflowTestReports.emailResults) {
    return (
      <Modal title="Email Results" onClose={onActionComplete}>
        <ModalBody>
          <Flex m={3} flexGrow={1} flexDirection="column">
            <EmailBackflowTestReportsResult
              result={data.emailBackflowTestReports}
            />
          </Flex>
        </ModalBody>
        <ModalFooter>
          <Button variant="outline" icon="x" onClick={onActionComplete}>
            Close
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  return (
    <Modal title={`Email Test Report${plural}`} onClose={onCancel}>
      <ModalBody>
        <Form>
          <FormRow>
            <FormColumn>
              <Label>Recipients</Label>
              <Label>
                <Checkbox
                  name="sendToCustomers"
                  checked={emailTestReportInput.sendToCustomers}
                  onClick={onInputChange}
                />
                Customer{plural}
              </Label>
              <Label>
                <Checkbox
                  name="sendToWaterSuppliers"
                  checked={emailTestReportInput.sendToWaterSuppliers}
                  onClick={onInputChange}
                />
                Water Supplier{plural}
              </Label>
              <Label>
                <Checkbox
                  name="bccMe"
                  checked={emailTestReportInput.bccMe}
                  onClick={onInputChange}
                />
                BCC Myself
              </Label>
            </FormColumn>
            <FormColumn>
              <Label htmlFor="cc" width="auto">
                CC
              </Label>
              <Textarea
                id="cc"
                height={68}
                autoFocus
                name="cc"
                value={emailTestReportInput.cc}
                placeholder="email1@domain.com
email2@domain.com
email3@domain.com"
                onChange={onInputChange}
              />
            </FormColumn>
          </FormRow>

          <FormColumn>
            <Label htmlFor="subject">Subject</Label>
            <Input
              type="text"
              value={emailTestReportInput.subject}
              width="100%"
              id="subject"
              name="subject"
              autoComplete="off"
              onChange={onInputChange}
            />
          </FormColumn>
          <FormColumn>
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              name="message"
              value={emailTestReportInput.message}
              height={100}
              onChange={onInputChange}
            />
          </FormColumn>
        </Form>
      </ModalBody>
      <ModalFooter justifyContent="space-between" alignItems="middle">
        <Label width="auto">
          <Checkbox
            name="setToSubmitted"
            checked={emailTestReportInput.setToSubmitted}
            onChange={onInputChange}
          />
          Set job{plural} status to "Submitted"
        </Label>
        <Flex sx={{ gap: 3 }}>
          <Button
            icon="mail"
            disabled={loading}
            onClick={() => {
              emailBackflowTestReports({
                variables: emailTestReportInput,
              });
            }}
          >
            {loading ? 'Sending...' : 'Send'}
          </Button>
          <Button
            variant="outline"
            icon="slash-circle"
            disabled={loading}
            onClick={onCancel}
          >
            Cancel
          </Button>
        </Flex>
      </ModalFooter>
    </Modal>
  );
};

EmailBackflowTestReportsModal.propTypes = {
  onClose: PropTypes.any,
  assignmentIds: PropTypes.any.isRequired,
};

export default EmailBackflowTestReportsModal;
